// Camera and pointer gestures, independent of panel selection and background renderer.
export default class Viewport {
    constructor(element) {
        this.element = element;
        this.stage = element.querySelector('.interactive-graphic__viewport');
        this.canvas = element.querySelector('.interactive-graphic__canvas');
        this.overview = element.querySelector('.interactive-graphic__overview');
        this.media = matchMedia('(max-width: 1199px)');
        this.abort = new AbortController();
        this.scale = 1;
        this.x = this.y = 0;
        this.focusX = this.focusY = 0;
        this.isOverview = false;
        const options = {signal: this.abort.signal};
        this.overview.addEventListener('click', () => {
            this.isOverview = true;
            this.reset();
            this.marker?.focus({preventScroll: true});
        }, options);
        this.stage.addEventListener('pointerdown', event => {
            if (!this.isMobile || this.scale === 1 || !event.isPrimary || event.button !== 0) return;
            this.drag = {id: event.pointerId, x: event.clientX, y: event.clientY, startX: this.x, startY: this.y};
            this.suppressClick = false;
        }, options);
        this.stage.addEventListener('pointermove', event => {
            if (!this.drag || this.drag.id !== event.pointerId) return;
            const dx = event.clientX - this.drag.x, dy = event.clientY - this.drag.y;
            if (!this.suppressClick && Math.hypot(dx, dy) < 6) return;
            this.suppressClick = true;
            this.stage.setPointerCapture(event.pointerId);
            this.stage.classList.add('is-dragging');
            this.x = this.drag.startX + dx;
            this.y = this.drag.startY + dy;
            this.apply();
        }, options);
        const finish = () => {
            this.drag = null;
            this.stage.classList.remove('is-dragging');
            // The synthesized click follows pointerup; leave suppression until the next task.
            clearTimeout(this.clickTimer);
            this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
        };
        this.stage.addEventListener('pointerup', finish, options);
        this.stage.addEventListener('pointercancel', finish, options);
        this.stage.addEventListener('lostpointercapture', finish, options);
        this.resize = new ResizeObserver(() => {
            if (this.marker && !this.isOverview) this.focus(this.markers, this.marker);
            else this.reset();
        });
        this.resize.observe(this.stage);
        this.resize.observe(this.canvas);
    }

    get isMobile() { return this.media.matches; }

    focus(markers, marker = markers?.[0]) {
        this.markers = markers;
        this.marker = marker;
        if (!markers?.length || !this.isMobile) { this.reset(); return; }
        this.isOverview = false;
        const coordinate = value => Math.max(0, Math.min(100, Number(value) || 0)) / 100;
        // Bubbles zoom with the canvas, so their radius is part of what has to fit.
        const radius = item => coordinate(item.dataset.r) * this.canvas.offsetWidth;
        const xs = markers.flatMap(item => [-1, 1].map(side => coordinate(item.dataset.x) * this.canvas.offsetWidth + side * radius(item)));
        const ys = markers.flatMap(item => [-1, 1].map(side => coordinate(item.dataset.y) * this.canvas.offsetHeight + side * radius(item)));
        const left = Math.min(...xs), right = Math.max(...xs);
        const top = Math.min(...ys), bottom = Math.max(...ys);
        const padding = 12;
        const fitX = right > left ? Math.max(1, this.stage.clientWidth - padding * 2) / (right - left) : Infinity;
        const fitY = bottom > top ? Math.max(1, this.stage.clientHeight - padding * 2) / (bottom - top) : Infinity;
        this.scale = Math.min(Math.max(1, Math.min(4, Number(this.element.dataset.zoom) || 2)), fitX, fitY);
        // Translation starts at the canvas' layout position, including viewport
        // padding and flex alignment, rather than at the viewport's top-left.
        this.x = this.stage.clientWidth / 2 - this.canvas.offsetLeft - (left + right) / 2 * this.scale;
        this.y = this.stage.clientHeight / 2 - this.canvas.offsetTop - (top + bottom) / 2 * this.scale;
        this.focusX = this.x;
        this.focusY = this.y;
        this.apply(false);
    }

    reset() {
        this.scale = 1;
        this.x = this.y = 0;
        this.focusX = this.focusY = 0;
        this.apply();
    }

    apply(clamp = true) {
        const width = this.canvas.offsetWidth * this.scale;
        const height = this.canvas.offsetHeight * this.scale;
        if (clamp) {
            // Edge bubbles can be centred beyond the canvas bounds. Keep that focus
            // position reachable while accounting for the canvas' layout offset.
            const minX = Math.min(0, this.stage.clientWidth - width - this.canvas.offsetLeft, this.focusX);
            const maxX = Math.max(0, -this.canvas.offsetLeft, this.focusX);
            const minY = Math.min(0, this.stage.clientHeight - height - this.canvas.offsetTop, this.focusY);
            const maxY = Math.max(0, -this.canvas.offsetTop, this.focusY);
            this.x = Math.max(minX, Math.min(maxX, this.x));
            this.y = Math.max(minY, Math.min(maxY, this.y));
        }
        this.canvas.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
        this.canvas.style.setProperty('--marker-scale', 1 / this.scale);
        this.stage.classList.toggle('is-zoomed', this.scale !== 1);
        this.overview.hidden = this.scale === 1;
    }

    destroy() {
        this.abort.abort();
        this.resize.disconnect();
        clearTimeout(this.clickTimer);
        this.reset();
    }
}
